import React, { useEffect, useState } from 'react';
import { sitesService, siteColour } from '../../_services/sites.service';
import useSites from './useSites';
import './multiLocation.css';

/** "Works at" — the person's sites in this group, plus one-tap "+ Add to <site>". Renders nothing for single-site salons. */
/** Hover text for a self-employed stylist's sharing at one location. */
function sharingTitle(sh) {
    const clients = sh.clientsShared ? 'Their clients are shared with the salon' : 'Their clients are private to them';
    const salon = sh.salonClientsShared ? 'they can see the salon\'s clients' : 'they see only their own clients';
    const diary = sh.diaryVisibility === 'nobody' ? 'their diary is hidden from the team' : sh.diaryVisibility === 'selected' ? 'their diary is visible to selected people' : 'their diary is visible to the team';
    return `${clients}; ${salon}; ${diary}.`;
}

export default function WorksAtChips({ userId }) {
    const { multi, isOwnerHere, canManageTeamHere } = useSites();
    const canManage = isOwnerHere || canManageTeamHere;
    const [info, setInfo] = useState(null);
    const [busy, setBusy] = useState(null);
    const [leaving, setLeaving] = useState(null);   // salonId awaiting a two-step confirm
    const [error, setError] = useState(null);
    const [notice, setNotice] = useState(null);     // what the last add/leave did
    const [leaveInfo, setLeaveInfo] = useState(null); // { salonId, futureBookings } for the confirm chip
    const load = () => sitesService.staffSites(userId).then(setInfo).catch(() => setInfo(null));
    useEffect(() => { if (multi && canManage && userId) load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [multi, canManage, userId]);
    if (!multi || !canManage || !info) return null;
    const add = async (s) => {
        setBusy(String(s.salonId)); setError(null); setNotice(null);
        try {
            const r = await sitesService.addStaffToSite({ userId, salonId: s.salonId, sourceSalonId: localStorage.getItem('salonId') });
            await load();
            if (r && r.rejoined) setNotice(`Back at ${s.salonName}. Set their hours there to make them bookable.`);
            else if (r && r.created) setNotice(`Added to ${s.salonName}${r.servicesCopied ? ` with ${r.servicesCopied} service${r.servicesCopied === 1 ? '' : 's'}` : ''}. Set their hours there to make them bookable.`);
            else setNotice(`They already work at ${s.salonName}.`);
        }
        catch (e) { setError(e.message || `Could not add them to ${s.salonName}`); }
        finally { setBusy(null); }
    };
    // Two-step: ask what leaving would leave behind (upcoming bookings there), then confirm.
    const askLeave = async (s) => {
        setLeaving(String(s.salonId)); setLeaveInfo(null); setError(null); setNotice(null);
        try {
            const p = await sitesService.staffLeaveSitePreview(userId, s.salonId);
            setLeaveInfo({ salonId: String(s.salonId), futureBookings: (p && p.futureBookings) || 0 });
        } catch (e) { /* the confirm still works; the count is a courtesy */ }
    };
    // "Left this location": retires their record, rota and access at ONE site; other sites, account and clients stay.
    const leave = async (s) => {
        setBusy(String(s.salonId)); setError(null); setNotice(null);
        try {
            const r = await sitesService.staffLeaveSite(userId, s.salonId);
            setLeaving(null); setLeaveInfo(null);
            await load();
            const n = (r && r.futureBookings) || 0;
            setNotice(n
                ? `Marked as left ${s.salonName}. They still have ${n} upcoming booking${n === 1 ? '' : 's'} there: move or cancel ${n === 1 ? 'it' : 'them'} from the diary.`
                : `Marked as left ${s.salonName}.`);
        }
        catch (e) { setError(e.message || 'Could not update'); }
        finally { setBusy(null); }
    };
    const pendingCount = (s) => (leaveInfo && leaveInfo.salonId === String(s.salonId) ? leaveInfo.futureBookings : 0);
    const canLeave = (s) => s.role !== 'owner' && info.worksAt.length > 1;
    return (
        <div className="es-field" style={{ gridColumn: '1 / -1' }}>
            <label className="es-label">Works at</label>
            <div className="ml-chip-row">
                {info.worksAt.map(s => (
                    leaving === String(s.salonId) ? (
                        <span key={String(s.salonId)} className="ml-chip" style={{ background: '#fdf2f1', color: '#8a2a21' }}>
                            Left {s.salonName}?{pendingCount(s) > 0 ? ` ${pendingCount(s)} upcoming booking${pendingCount(s) === 1 ? '' : 's'} there.` : ''}
                            <button type="button" className="ml-chip-x" onClick={() => leave(s)} disabled={!!busy} style={{ marginLeft: 6, fontWeight: 600 }}>{busy === String(s.salonId) ? '…' : 'Yes'}</button>
                            <button type="button" className="ml-chip-x" onClick={() => { setLeaving(null); setLeaveInfo(null); }} disabled={!!busy}>No</button>
                        </span>
                    ) : (
                        <span key={String(s.salonId)} className="ml-chip" title={s.sharing ? sharingTitle(s.sharing) : undefined}>
                            <span className="ml-dot" style={{ background: siteColour(s.salonId) }} />{s.salonName}{s.role === 'owner' ? ' · owner' : ''}
                            {s.sharing && <span className="ml-muted" style={{ marginLeft: 6 }}>{s.sharing.clientsShared ? 'clients shared' : 'clients private'}</span>}
                            {canLeave(s) && <button type="button" className="ml-chip-x" aria-label={`Left ${s.salonName}`} title="Left this location" onClick={() => askLeave(s)}>×</button>}
                        </span>
                    )
                ))}
                {info.canAddTo.map(s => (
                    <button key={String(s.salonId)} type="button" className="ml-chip ml-chip--action" onClick={() => add(s)} disabled={!!busy}>
                        {busy === String(s.salonId) ? 'Adding…' : `+ Add to ${s.salonName}`}
                    </button>
                ))}
            </div>
            {info.elsewhere && info.elsewhere.length > 0 && <div className="ml-muted" style={{ marginTop: 4 }}>Also works at {info.elsewhere.map(e => e.salonName).join(', ')} (outside your locations).</div>}
            {error && <div className="ml-muted" role="alert" style={{ marginTop: 4, color: '#B14438' }}>{error}</div>}
            {notice && !error && <div className="ml-muted" role="status" style={{ marginTop: 4 }}>{notice}</div>}
            <div className="ml-muted" style={{ marginTop: 4 }}>Adding copies their services and prices from here. Commission, rota and pay stay per location. × marks them as left that location only.</div>
            {info.worksAt.some(s => s.sharing) && <div className="ml-muted" style={{ marginTop: 4 }}>A self-employed stylist added to another location keeps the client sharing they chose here. They're told, and can change it in their Settings.</div>}
        </div>
    );
}
